import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { adminFeedApi } from '../feedApi';
import { pollOptionsFromItem, validatePollInput, buildPollPayload } from '../utils/poll';

function buildFormFromItem(item) {
  if (!item) {
    return {
      title: '',
      description: '',
      article_url: '',
      is_pinned: false,
      hasPoll: false,
      pollQuestion: '',
      pollOptions: ['', ''],
    };
  }

  const body = item.description ?? item.body ?? '';
  const url = item.article_url ?? item.external_url ?? '';
  const poll = item.poll || null;

  return {
    title: item.title || '',
    description: body || '',
    article_url: url || '',
    is_pinned: !!item.is_pinned,
    hasPoll: !!poll,
    pollQuestion: poll?.question || '',
    pollOptions: pollOptionsFromItem(poll),
  };
}

/**
 * Wrapper component to avoid eslint `react-hooks/set-state-in-effect`.
 * We remount the inner dialog when the item changes, so form state is initialized from props
 * without using useEffect.
 */
export default function EditFeedItemDialog(props) {
  const key = `${props?.item?.id || 'no-item'}:${props.open ? 'open' : 'closed'}`;
  return <EditFeedItemDialogInner key={key} {...props} />;
}

function EditFeedItemDialogInner({ open, onOpenChange, groupId, item, onUpdated }) {
  const { t } = useTranslation();

  const [form, setForm] = useState(() => buildFormFromItem(item));

  const updatePollOption = (index, value) => {
    setForm((prev) => {
      const arr = [...prev.pollOptions];
      arr[index] = value;
      return { ...prev, pollOptions: arr };
    });
  };

  const addPollOption = () =>
    setForm((p) => ({ ...p, pollOptions: [...p.pollOptions, ''] }));

  const removePollOption = (index) =>
    setForm((p) => ({
      ...p,
      pollOptions: p.pollOptions.filter((_, i) => i !== index),
    }));

  const handleSave = async () => {
    if (!item?.id) return;

    if (!form.title.trim()) {
      toast.error(
        t('groupAdminTabs.feed.toastTitleRequired', {
          defaultValue: 'Title is required',
        })
      );
      return;
    }

    if (form.hasPoll) {
      const v = validatePollInput(form.pollQuestion, form.pollOptions);
      if (!v.ok) {
        toast.error(
          t('groupAdminTabs.feed.toastPollInvalid', {
            defaultValue: v.message,
          })
        );
        return;
      }
    }

    try {
      const payload = {
        title: form.title.trim(),
        description: (form.description || '').trim(),
        article_url: (form.article_url || '').trim(),
        is_pinned: !!form.is_pinned,
      };

      payload.poll = form.hasPoll
        ? buildPollPayload(form.pollQuestion, form.pollOptions, { multiple: false })
        : null;

      await adminFeedApi.updateFeedItem(groupId, item.id, payload);

      toast.success(
        t('groupAdminTabs.feed.toastUpdateSuccess', {
          defaultValue: 'Feed item updated',
        })
      );
      onOpenChange(false);
      onUpdated?.();
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.detail ||
          t('groupAdminTabs.feed.toastUpdateError', {
            defaultValue: 'Failed to update feed item',
          })
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('groupAdminTabs.feed.editTitle', {
              defaultValue: 'Edit feed item',
            })}
          </DialogTitle>
          <DialogDescription>
            {t('groupAdminTabs.feed.editDescription', {
              defaultValue: 'Update the content of this post.',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>
              {t('groupAdminTabs.feed.titleField', { defaultValue: 'Title' })}
            </Label>
            <Input
              value={form.title}
              onChange={(e) =>
                setForm((p) => ({ ...p, title: e.target.value }))
              }
            />
          </div>

          <div>
            <Label>
              {t('groupAdminTabs.feed.descriptionField', {
                defaultValue: 'Description',
              })}
            </Label>
            <Textarea
              rows={4}
              value={form.description}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label>
              {t('groupAdminTabs.feed.articleUrl', {
                defaultValue: 'Article URL',
              })}{' '}
              <span className="text-xs text-slate-500">
                {t('groupAdminTabs.common.optional', { defaultValue: '(optional)' })}
              </span>
            </Label>
            <Input
              value={form.article_url}
              onChange={(e) =>
                setForm((p) => ({ ...p, article_url: e.target.value }))
              }
              placeholder="https://example.com/article"
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium">
                {t('groupAdminTabs.feed.pollToggleLabel', {
                  defaultValue: 'Include poll',
                })}
              </p>
              <p className="text-xs text-slate-500">
                {t('groupAdminTabs.feed.pollToggleHint', {
                  defaultValue: 'Add a question and options.',
                })}
              </p>
            </div>
            <Switch
              checked={form.hasPoll}
              onCheckedChange={(v) =>
                setForm((p) => ({ ...p, hasPoll: v }))
              }
            />
          </div>

          {form.hasPoll && (
            <div className="space-y-3 rounded-lg border p-3">
              <div>
                <Label>
                  {t('groupAdminTabs.feed.pollQuestion', {
                    defaultValue: 'Poll question',
                  })}
                </Label>
                <Input
                  value={form.pollQuestion}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, pollQuestion: e.target.value }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>
                  {t('groupAdminTabs.feed.pollOptions', {
                    defaultValue: 'Options',
                  })}
                </Label>

                {form.pollOptions.map((opt, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      value={opt}
                      onChange={(e) => updatePollOption(idx, e.target.value)}
                      placeholder={t('groupAdminTabs.feed.pollOptionPlaceholder', { num: idx + 1, defaultValue: `Option ${idx + 1}` })}
                    />
                    {form.pollOptions.length > 2 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => removePollOption(idx)}
                      >
                        <X size={14} />
                      </Button>
                    )}
                  </div>
                ))}

                <Button type="button" variant="outline" onClick={addPollOption}>
                  <Plus size={14} className="mr-2" />
                  {t('groupAdminTabs.feed.addOption', {
                    defaultValue: 'Add option',
                  })}
                </Button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium">
                {t('groupAdminTabs.feed.pinLabel', { defaultValue: 'Pin post' })}
              </p>
              <p className="text-xs text-slate-500">
                {t('groupAdminTabs.feed.pinHint', {
                  defaultValue: 'Pinned posts can show at the top.',
                })}
              </p>
            </div>
            <Switch
              checked={form.is_pinned}
              onCheckedChange={(v) =>
                setForm((p) => ({ ...p, is_pinned: v }))
              }
            />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('groupAdminTabs.common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={handleSave}>
            {t('groupAdminTabs.common.save', { defaultValue: 'Save' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
